import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "diamonds-search-form w-full mx-auto p-4 pt-0" }
const _hoisted_2 = {
  key: 0,
  class: "flex justify-center mb-10"
}
const _hoisted_3 = { class: "relative bg-gray-100 p-1 rounded-xl flex w-full shadow-inner" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 2 }
const _hoisted_6 = { class: "mt-8 flex justify-center mb-10" }

import { ref, computed } from "vue";
import DiamondsSearchFormSimple from "@/components/diamonds/form/diamondsSearchFormSimple.vue";
import DiamondsSearchFormPro from "@/components/diamonds/form/diamondsSearchFormPro.vue";
import {useI18n} from "vue-i18n";


import { presets, getActiveValueLabelShared } from "@/assets/ts/diamondFilters";
export { presets, getActiveValueLabelShared };

export default /*@__PURE__*/_defineComponent({
  __name: 'diamondsSearchForm',
  props: {
    filters: {},
    activeFilters: {},
    singleFilterKey: {},
    simpleFilterKeys: {},
    searchMode: { default: 'simple' }
  },
  emits: ["update:filters", "updateFilters", "update:searchMode"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();
const props = __props;

const emit = __emit;

const internalSearchMode = ref<'simple' | 'pro'>(props.searchMode);
const searchMode = computed({
  get: () => props.searchMode || internalSearchMode.value,
  set: (val: 'simple' | 'pro') => {
    internalSearchMode.value = val;
    emit('update:searchMode', val);
  }
});
const manualExpanded = ref<Record<string, boolean>>({});

const updateFilters = (value: any) => {
  emit("update:filters", value);
}

const switchToSimple = () => {
  searchMode.value = 'simple';
  const newFilters = { ...props.activeFilters };
  const simpleKeys = props.simpleFilterKeys || ['type', 'size', 'form'];

  Object.keys(props.filters).forEach(key => {
    if (!simpleKeys.includes(key)) {
      newFilters[key] = props.filters[key].default !== undefined ? props.filters[key].default : null;
    }
  });

  emit("update:filters", newFilters);
};

const isFilterDefined = (key: string) => {
  const filter = props.filters[key];
  const active = props.activeFilters[key];
  if (!active) return false;
  // Handle objects (like carat or price range)
  if (typeof active === 'object' && filter.default && typeof filter.default === 'object') {
    return active.min !== filter.default.min || active.max !== filter.default.max;
  }
  if (Array.isArray(active) && Array.isArray(filter.default)) {
    return active.length !== filter.default.length || !active.every((v, i) => v === filter.default[i]);
  }
  if (filter.default && active === filter.default) return false;
  return true;
};

const isFilterOpen = (key: string) => {
  if (isFilterDefined(key)) {
    return true;
  }
  if (manualExpanded.value[key] !== undefined) {
    return manualExpanded.value[key];
  }
  return props.filters[key]?.visible || false;
};

const toggleFilter = (key: string) => {
  if (isFilterDefined(key)) return; // Always open if defined
  manualExpanded.value[key] = !isFilterOpen(key);
};

const filteredPresets = (activeFilters: { type: any; stone_type: any; form: any; stone_form: any; }, rawPresets: any[] = []) => {
  const stoneType = (activeFilters.type ?? activeFilters.stone_type) as string | undefined;
  const stoneForm = (activeFilters.form ?? activeFilters.stone_form) as string | undefined;

  return rawPresets.filter((preset) => preset.filters.type === stoneType && preset.forms.includes(stoneForm));
};

const isPresetActive = (activeFilters: { [x: string]: any; }, presetFilters: any) => {
  return Object.entries(presetFilters).every(([key, value]) => {
    const activeValue = activeFilters[key];
    if (Array.isArray(value) && Array.isArray(activeValue)) {
      return value.length === activeValue.length && value.every(v => activeValue.includes(v));
    }
    return activeValue === value;
  });
};

const updateFilter = (key: string, value: any) => {
  let newFilters = { ...props.activeFilters };
  const filter = props.filters[key];

  let parsedValue = value;
  if (filter.type === 'bool') {
    if (value === 'true') parsedValue = true;
    else if (value === 'false') parsedValue = false;
  }

  const oldValue = newFilters[key];

  // If it's the new list component, we always want the array value it emits
  if (filter.type === 'text' && filter.view === 'list') {
    newFilters[key] = value;
  } else if (newFilters[key] === parsedValue) {
    newFilters[key] = filter.default !== undefined ? filter.default : null;
  } else {
    newFilters[key] = parsedValue;
  }

  // Special rule sets
  if (key === 'type' && oldValue === 'earth' && newFilters.type === 'lab') {
    newFilters.institute = [];
    newFilters.color = ["E", "D"];
    newFilters.clarity = ["VS2", "VS1", "VVS2", "VVS1", "IF", "FL"];
  }

  if (key === 'type' && oldValue === 'lab' && newFilters.type === 'earth' && newFilters.colored === true) {
    newFilters.colored = false;
    newFilters.color = [];
    newFilters.fancy_intensity = [];
  }

  if (key === 'form' && oldValue === 'BR' && newFilters.form !== 'BR') {
    newFilters.clarity = ["VS2", "VS1", "VVS2", "VVS1", "IF", "FL"];
    newFilters.symmetry = ["VG", "EX"];
    newFilters.polish = ["VG", "EX"];
  }

  if (key === 'colored' && (oldValue === false || oldValue == null) && newFilters.colored === true) {
    newFilters.type = "lab";
    newFilters.clarity = [];
    newFilters.color = [];
    newFilters.cut = [];
    newFilters.polish = [];
    newFilters.fancy_intensity = [];
    newFilters.symmetry = [];
    newFilters.fluorescence = [];
    newFilters.institute = [];
    newFilters.fastDelivery = false;
  }

  if (key === 'form' && newFilters.form !== "BR") {
    newFilters.cut = [];
  }

  if ((key == "form" || key == "type") && !(typeof newFilters.colored == "boolean" && newFilters.colored === true)) {
    const presetFilters = filteredPresets(newFilters, presets)[0];
    let activePreset = false;

    for (const preset of presets) {
      activePreset = activePreset || isPresetActive(newFilters, preset.filters);
    }

    if (!activePreset) {

      newFilters = { ...newFilters, ...presetFilters.filters };
    }
  }

  emit("update:filters", newFilters);
};

const getFilterLabel = (key: string) => {
  return t('components.diamonds.search.labels.' + key);
};

const getActiveValueLabel = (key: string, value: any) => {
  return getActiveValueLabelShared(key, value, props.filters, props.activeFilters);
};

const resetFilters = () => {
  const newFilters: Record<string, any> = {};
  Object.keys(props.filters).forEach((key) => {
    const def = props.filters[key]?.default;
    newFilters[key] = def !== undefined ? def : null;
  });
  emit("update:filters", newFilters);
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    false
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", {
              class: "absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-lg shadow-sm transition-all duration-300 ease-out transform",
              style: _normalizeStyle({ left: searchMode.value === 'pro' ? 'calc(50% + 2px)' : '4px' })
            }, null, 4),
            _createElementVNode("button", {
              type: "button",
              onClick: switchToSimple,
              class: _normalizeClass(["relative flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors duration-200 z-10", searchMode.value === 'simple' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'])
            }, [...(_cache[1] || (_cache[1] = [
              _createElementVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                class: "w-4 h-4",
                fill: "none",
                viewBox: "0 0 24 24",
                stroke: "currentColor",
                "stroke-width": "2"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  d: "M4 6h16M4 12h16m-7 6h7"
                })
              ], -1),
              _createTextVNode(" Einfach ", -1)
            ]))], 2),
            _createElementVNode("button", {
              type: "button",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (searchMode.value = 'pro')),
              class: _normalizeClass(["relative flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors duration-200 z-10", searchMode.value === 'pro' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'])
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                class: "w-4 h-4",
                fill: "none",
                viewBox: "0 0 24 24",
                stroke: "currentColor",
                "stroke-width": "2"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  d: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                })
              ], -1),
              _createTextVNode(" Erweitert ", -1)
            ]))], 2)
          ])
        ]))
      : _createCommentVNode("", true),
    (searchMode.value === 'simple')
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(DiamondsSearchFormSimple, {
            filters: __props.filters,
            "active-filters": __props.activeFilters,
            "simple-filter-keys": __props.simpleFilterKeys,
            "get-filter-label": getFilterLabel,
            "get-active-value-label": getActiveValueLabel,
            "is-filter-defined": isFilterDefined,
            presets: _unref(presets),
            onUpdateFilter: updateFilter,
            onUpdateFilters: updateFilters
          }, null, 8, ["filters", "active-filters", "simple-filter-keys", "presets"])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(DiamondsSearchFormPro, {
            filters: __props.filters,
            "active-filters": __props.activeFilters,
            "single-filter-key": __props.singleFilterKey,
            "get-filter-label": getFilterLabel,
            "get-active-value-label": getActiveValueLabel,
            "is-filter-defined": isFilterDefined,
            "is-filter-open": isFilterOpen,
            "toggle-filter": toggleFilter,
            presets: _unref(presets),
            onUpdateFilter: updateFilter,
            onUpdateFilters: updateFilters
          }, null, 8, ["filters", "active-filters", "single-filter-key", "presets"])
        ])),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("button", {
        onClick: resetFilters,
        class: "text-xs uppercase tracking-widest text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-2"
      }, [
        _cache[3] || (_cache[3] = _createElementVNode("svg", {
          xmlns: "http://www.w3.org/2000/svg",
          class: "h-4 w-4",
          fill: "none",
          viewBox: "0 0 24 24",
          stroke: "currentColor"
        }, [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "stroke-width": "2",
            d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          })
        ], -1)),
        _createTextVNode(" " + _toDisplayString(_unref(t)('components.catalog.filterSideBar.cta.reset')), 1)
      ])
    ])
  ]))
}
}

})